import React from "react";
import { clsx } from "clsx";
import { Inbox } from "lucide-react";
import { Button } from "../Button";

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
    title: string;
    description?: string;
    icon?: React.ReactNode;
    actionLabel?: string;
    onAction?: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
    title,
    description,
    icon,
    actionLabel,
    onAction,
    className,
    ...props
}) => (
    <div
        className={clsx(
            "flex flex-col items-center justify-center rounded-mz-xl border border-dashed border-neutral-border px-6 py-12 text-center dark:border-white/15",
            className,
        )}
        {...props}
    >
        <div className="mb-4 text-neutral-secondary">{icon ?? <Inbox size={32} aria-hidden />}</div>
        <h3 className="font-display text-lg font-semibold text-neutral-dark dark:text-white">{title}</h3>
        {description ? <p className="mt-1 max-w-sm text-sm text-neutral-secondary">{description}</p> : null}
        {actionLabel && onAction ? (
            <Button className="mt-4" size="sm" onClick={onAction}>
                {actionLabel}
            </Button>
        ) : null}
    </div>
);

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
    title?: string;
    description?: string;
    onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
    title = "Something went wrong",
    description = "We could not load this content. Try again.",
    onRetry,
    className,
    ...props
}) => (
    <EmptyState
        title={title}
        description={description}
        actionLabel={onRetry ? "Retry" : undefined}
        onAction={onRetry}
        className={clsx("border-danger/30 bg-danger/5", className)}
        {...props}
    />
);
